import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';
import { courseService } from '../../services/courseService';
import { publicCourse } from '../../data/courseData';
import { selectIsAuthenticated } from '../../redux/slices/authSlice';
import {
  PlayerPage,
  PlayerWrapper,
  PlayerHeader,
  VideoBox,
  CompleteButton,
  StatusBox,
} from './Curso.styles';

function formatDateBR(iso) {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

const Aula = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const [lesson, setLesson] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Estado para navegação entre aulas
  const [currentModule, setCurrentModule] = useState(null);
  const [lessonIndex, setLessonIndex] = useState(null);
  const [previousLesson, setPreviousLesson] = useState(null);
  const [nextLesson, setNextLesson] = useState(null);

  const playerRef = useRef(null);
  const plyrRef = useRef(null);
  const hasMarkedAsCompletedOnPlayRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Reset o flag quando muda de aula
    hasMarkedAsCompletedOnPlayRef.current = false;
    setLesson(null);
    setCurrentModule(null);
    setLessonIndex(null);
    setPreviousLesson(null);
    setNextLesson(null);

    // getLesson roda com optionalAuth no backend — token vai se houver,
    // mas a aula grátis funciona sem.
    const calls = [courseService.getLesson(token, lessonId)];
    if (isAuthenticated) {
      calls.push(courseService.getAccess(token));
    }

    Promise.all(calls)
      .then(([lessonData, accessData]) => {
        if (cancelled) return;
        
        // Carregar dados da aula
        setLesson(lessonData.lesson);
        if (accessData) {
          setCompleted((accessData.completedLessonIds || []).includes(lessonId));
        }

        // Carregar curso para navegação
        const courseData = publicCourse();

        // Encontrar o módulo e índice da aula atual
        let foundModule = null;
        let foundIndex = null;
        
        for (const module of courseData.modules || []) {
          const idx = module.lessons?.findIndex(l => l.id === lessonId);
          if (idx !== -1) {
            foundModule = module;
            foundIndex = idx;
            break;
          }
        }

        if (foundModule && foundIndex !== null) {
          setCurrentModule(foundModule);
          setLessonIndex(foundIndex);

          // Calcular aula anterior
          if (foundIndex > 0) {
            setPreviousLesson(foundModule.lessons[foundIndex - 1]);
          } else {
            // Se é a primeira do módulo, ver última do módulo anterior
            const moduleIdx = courseData.modules.findIndex(m => m.id === foundModule.id);
            if (moduleIdx > 0) {
              const prevModule = courseData.modules[moduleIdx - 1];
              if (prevModule.lessons?.length > 0) {
                setPreviousLesson(prevModule.lessons[prevModule.lessons.length - 1]);
              } else {
                setPreviousLesson(null);
              }
            } else {
              setPreviousLesson(null);
            }
          }

          // Calcular próxima aula
          if (foundIndex < foundModule.lessons.length - 1) {
            setNextLesson(foundModule.lessons[foundIndex + 1]);
          } else {
            // Se é a última do módulo, ver primeira do próximo
            const moduleIdx = courseData.modules.findIndex(m => m.id === foundModule.id);
            if (moduleIdx < courseData.modules.length - 1) {
              const nextModule = courseData.modules[moduleIdx + 1];
              if (nextModule.lessons?.length > 0) {
                setNextLesson(nextModule.lessons[0]);
              }
            } else {
              setNextLesson(null);
            }
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 401) {
          setError({
            kind: 'login',
            message: 'Entre na sua conta para assistir a esta aula.',
          });
        } else if (err.status === 403) {
          setError({
            kind: 'buy',
            message: 'Você precisa comprar o curso para assistir a esta aula.',
          });
        } else if (err.status === 423) {
          const date = err.data?.releaseDate;
          setError({
            kind: 'locked',
            message: date
              ? `Esta aula será liberada em ${formatDateBR(date)}.`
              : 'Esta aula ainda não foi liberada.',
          });
        } else if (err.status === 404) {
          setError({ kind: 'notfound', message: 'Aula não encontrada.' });
        } else {
          setError({ kind: 'error', message: err.message || 'Falha ao carregar aula.' });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token, lessonId]);

  useEffect(() => {
    if (lesson?.youtubeId && playerRef.current) {
      plyrRef.current = new Plyr(playerRef.current, {
        controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
        youtube: {
          noCookie: true,
          rel: 0,
          modestbranding: 1,
          iv_load_policy: 3,
        },
      });

      // Marcar como concluído na primeira vez que o usuário der play.
      // Não usa `completed` aqui para não recriar o player ao mudar o estado.
      const handlePlay = async () => {
        if (!hasMarkedAsCompletedOnPlayRef.current) {
          hasMarkedAsCompletedOnPlayRef.current = true;
          try {
            const result = await courseService.toggleLessonComplete(token, lessonId);
            setCompleted(result.completed);
          } catch (err) {
            console.warn('Falha ao marcar aula como concluída:', err.message);
            hasMarkedAsCompletedOnPlayRef.current = false;
          }
        }
      };

      plyrRef.current.on('play', handlePlay);

      return () => {
        if (plyrRef.current) {
          plyrRef.current.off('play', handlePlay);
          plyrRef.current.destroy();
          plyrRef.current = null;
        }
      };
    }

    return () => {
      if (plyrRef.current) {
        plyrRef.current.destroy();
        plyrRef.current = null;
      }
    };
  }, [lesson, token, lessonId]);

  const handleToggleComplete = async () => {
    try {
      const result = await courseService.toggleLessonComplete(token, lessonId);
      setCompleted(result.completed);
    } catch (err) {
      console.warn('Falha ao marcar aula:', err.message);
    }
  };

  if (loading) {
    return (
      <PlayerPage>
        <PlayerWrapper>
          <p>Carregando…</p>
        </PlayerWrapper>
      </PlayerPage>
    );
  }

  if (error) {
    return (
      <PlayerPage>
        <PlayerWrapper>
          <PlayerHeader>
            <Link to="/curso-de-fingerstyle">← Voltar ao curso</Link>
          </PlayerHeader>
          <StatusBox>{error.message}</StatusBox>
          {error.kind === 'login' && (
            <p style={{ textAlign: 'center', marginTop: 16 }}>
              <Link to="/login" state={{ from: `/curso-de-fingerstyle/aula/${lessonId}` }}>
                Entrar
              </Link>
            </p>
          )}
          {error.kind === 'buy' && (
            <p style={{ textAlign: 'center', marginTop: 16 }}>
              <Link to="/curso-de-fingerstyle" state={{ from: `/curso-de-fingerstyle/aula/${lessonId}` }}>
                Ver detalhes e comprar →
              </Link>
            </p>
          )}
        </PlayerWrapper>
      </PlayerPage>
    );
  }

  // Aula grátis (free module) vista por usuário não autenticado:
  // não mostra o botão "Marcar concluída".
  const canMarkComplete = isAuthenticated;

  // Calcular total de aulas do módulo e posição atual
  const totalLessonsInModule = currentModule?.lessons?.length || 0;
  const currentLessonPosition = lessonIndex !== null ? lessonIndex + 1 : 0;

  return (
    <PlayerPage>
      <PlayerWrapper>
        <PlayerHeader>
          <Link to="/curso-de-fingerstyle">←</Link>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <h1>{lesson?.title}</h1>
            {currentModule && (
              <p style={{ color: '#888', fontSize: '13px', marginTop: '6px' }}>
                {currentModule.title} • Aula {currentLessonPosition} de {totalLessonsInModule}
              </p>
            )}
          </div>
          {canMarkComplete && (
            <CompleteButton $done={completed} onClick={handleToggleComplete}>
              {completed ? '✓ Concluída' : 'Marcar concluída'}
            </CompleteButton>
          )}
        </PlayerHeader>

        <VideoBox>
          {lesson?.youtubeId && lesson.youtubeId !== 'TODO_YT_ID' ? (
            <div
              key={lessonId}
              ref={playerRef}
              data-plyr-provider="youtube"
              data-plyr-embed-id={lesson.youtubeId}
            />
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: '240px',
              gap: '12px',
              color: 'rgba(255,255,255,0.4)',
            }}>
              <span style={{ fontSize: '48px' }}>🎬</span>
              <p style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: 'rgba(255,255,255,0.6)' }}>Vídeo em breve</p>
              <p style={{ margin: 0, fontSize: '13px' }}>Esta aula será disponibilizada em breve.</p>
            </div>
          )}
        </VideoBox>

        <p style={{ color: '#aaa', fontSize: 13, marginTop: '16px' }}>
          Módulo: <strong style={{ color: '#fff' }}>{lesson?.moduleTitle || currentModule?.title}</strong>
        </p>

        {/* Barra de Progresso do Módulo */}
        {currentModule && (
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <div style={{
              height: '6px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '3px',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                backgroundColor: '#ffd700',
                width: `${(currentLessonPosition / totalLessonsInModule) * 100}%`,
                transition: 'width 0.3s ease',
              }} />
            </div>
            <p style={{ color: '#888', fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
              Progresso: {currentLessonPosition} de {totalLessonsInModule} aulas
            </p>
          </div>
        )}

        {/* Navegação entre Aulas */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px',
          marginTop: '24px',
        }}>
          {previousLesson ? (
            <button
              onClick={() => navigate(`/curso-de-fingerstyle/aula/${previousLesson.id}`)}
              style={{
                padding: '12px 16px',
                backgroundColor: 'rgba(255, 215, 0, 0.15)',
                color: '#ffd700',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 215, 0, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 215, 0, 0.15)';
              }}
            >
              ← Aula anterior
            </button>
          ) : (
            <div style={{
              padding: '12px 16px',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: '#666',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              textAlign: 'left',
            }}>
              ← Primeira aula
            </div>
          )}

          {nextLesson ? (
            <button
              onClick={() => navigate(`/curso-de-fingerstyle/aula/${nextLesson.id}`)}
              style={{
                padding: '12px 16px',
                backgroundColor: '#ffd700',
                color: '#1a1a1a',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s',
                textAlign: 'right',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#ffed4e';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#ffd700';
              }}
            >
              Próxima aula →
            </button>
          ) : (
            <div style={{
              padding: '12px 16px',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: '#666',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              textAlign: 'right',
            }}>
              Última aula →
            </div>
          )}
        </div>

        {/* Lista de Aulas do Módulo (opcional, mas útil para navegação rápida) */}
        {currentModule && currentModule.lessons?.length > 0 && (
          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(255, 215, 0, 0.1)' }}>
            <h3 style={{ fontSize: '14px', color: '#ffd700', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Aulas deste módulo
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflowY: 'auto' }}>
              {currentModule.lessons.map((l, idx) => (
                <button
                  key={l.id}
                  onClick={() => navigate(`/curso-de-fingerstyle/aula/${l.id}`)}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: l.id === lessonId ? 'rgba(255, 215, 0, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                    color: l.id === lessonId ? '#ffd700' : '#aaa',
                    border: `1px solid ${l.id === lessonId ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    textAlign: 'left',
                    fontWeight: l.id === lessonId ? '600' : '400',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (l.id !== lessonId) {
                      e.target.style.backgroundColor = 'rgba(255, 215, 0, 0.1)';
                      e.target.style.color = '#ffd700';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (l.id !== lessonId) {
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                      e.target.style.color = '#aaa';
                    }
                  }}
                >
                  <span style={{ display: 'inline-block', minWidth: '24px' }}>{idx + 1}.</span>
                  <span>{l.title}</span>
                  {completed && l.id === lessonId && <span style={{ marginLeft: 'auto' }}>✓</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </PlayerWrapper>
    </PlayerPage>
  );
};

export default Aula;
